import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, ArrowUpRight, Plus, Check } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useCompare } from "../contexts/CompareContext";
import { productById } from "../data/products";
import { hasVideo, videoMap, youtubeThumbnail } from "../data/videoMap";
import { generateTelegramLink } from "../data/telegramLinks";

const ACCENT_COLORS = {
  gold: "#D7B56D",
  bronze: "#A9824A",
  cyan: "#76D9E8",
  silver: "#D8DCE2",
  emerald: "#3A7762",
  violet: "#7B6FA8",
  amber: "#D59A4A",
};

export const ProductModal = ({ openId, onClose, onPlay }) => {
  const { t, productContent } = useLanguage();
  const { items, toggle } = useCompare();
  const product = openId ? productById(openId) : null;

  React.useEffect(() => {
    if (openId) {
      const onKey = (e) => e.key === "Escape" && onClose();
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      };
    }
  }, [openId, onClose]);

  if (!product) return null;
  const accent = ACCENT_COLORS[product.accent] || ACCENT_COLORS.gold;
  const content = productContent(product.id);
  const inCompare = items.includes(product.id);
  const video = hasVideo(product.id);
  const thumb = video ? youtubeThumbnail(videoMap[product.id]) : null;
  const initials = product.name.split(" ").map((w) => w[0]).join("").slice(0, 3);

  return (
    <AnimatePresence>
      {openId && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-0 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="product-modal"
        >
          <div className="absolute inset-0 bg-[#07090D]/85 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full md:max-w-[1100px] h-full md:h-[90vh] max-h-[100vh] md:max-h-[860px] glass-strong md:rounded-[28px] overflow-hidden flex flex-col md:flex-row"
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-[#1A1C22]/80 backdrop-blur border border-[#D8DCE2]/15 flex items-center justify-center hover:border-[#D7B56D]/40 transition-colors"
              data-testid="product-modal-close"
              aria-label={t("close")}
            >
              <X size={16} className="text-[#F5F1EA]" />
            </button>

            {/* Left visual */}
            <div className="md:w-[44%] relative md:border-r border-[#D8DCE2]/8 shrink-0">
              <div
                className="h-[280px] md:h-full relative overflow-hidden"
                style={{
                  background: `radial-gradient(circle at 30% 20%, ${accent}26, transparent 55%), linear-gradient(180deg, #1a1d24, #0e1015)`,
                }}
              >
                <svg className="absolute inset-0 w-full h-full opacity-45" viewBox="0 0 400 600" fill="none" aria-hidden>
                  <defs>
                    <linearGradient id={`mgr-${product.id}`} x1="0" y1="0" x2="400" y2="600">
                      <stop offset="0" stopColor={accent} stopOpacity="0.4" />
                      <stop offset="1" stopColor={accent} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <circle cx="80" cy="120" r="60" fill="none" stroke={`url(#mgr-${product.id})`} strokeWidth="0.8" />
                  <circle cx="320" cy="380" r="90" fill="none" stroke={`url(#mgr-${product.id})`} strokeWidth="0.8" />
                  <path
                    d={`M 0 ${280 + product.rank * 4} Q 100 ${230}, 200 ${280} T 400 ${260}`}
                    stroke={accent}
                    strokeWidth="1"
                    strokeOpacity="0.42"
                    fill="none"
                  />
                </svg>
                <div className="absolute inset-0 grain-overlay" />

                <div className="absolute top-6 left-6 right-6 flex items-start gap-3">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center font-mono text-base font-bold glass border"
                    style={{ borderColor: `${accent}55`, color: accent }}
                  >
                    {initials}
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#D8DCE2]/60">
                      Module #{String(product.rank).padStart(2, "0")}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] mt-1" style={{ color: accent }}>
                      {product.importance}
                    </div>
                  </div>
                </div>

                {video && (
                  <button
                    onClick={() => onPlay(product.id)}
                    className="absolute bottom-6 left-6 right-6 group flex items-center gap-3 p-3 rounded-2xl glass-strong border border-[#76D9E8]/30 hover:border-[#76D9E8] transition-colors text-start"
                    data-testid="modal-video-btn"
                  >
                    <div className="relative w-20 h-14 rounded-xl overflow-hidden shrink-0 bg-[#1A1C22]">
                      {thumb && <img src={thumb} alt="" className="w-full h-full object-cover" />}
                      <div className="absolute inset-0 bg-[#07090D]/40 flex items-center justify-center">
                        <Play size={18} className="text-[#76D9E8]" fill="#76D9E8" />
                      </div>
                    </div>
                    <div>
                      <div className="font-display text-[14px] font-bold text-[#F5F1EA] leading-tight">
                        {t("videoText")}
                      </div>
                      <div className="text-[11px] text-[#D8DCE2]/55 mt-1">{t("videoSub")}</div>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Right content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-9">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: accent }}>
                {product.categoryLabel}
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-[#F5F1EA] leading-tight mb-4" data-testid="modal-product-title">
                {product.name}
              </h2>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {product.badges.map((b) => (
                  <span key={b} className="tag" style={{ borderColor: `${accent}40`, color: accent }}>
                    {b}
                  </span>
                ))}
              </div>

              <p className="text-[14px] text-[#E6E0D6] leading-relaxed mb-7">{content?.long}</p>

              {/* Meta */}
              <div className="grid grid-cols-2 gap-4 mb-7 pb-7 border-b border-[#D8DCE2]/10">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A9824A] mb-1.5">{t("cardLevel")}</div>
                  <div className="text-[13px] text-[#F5F1EA]">{product.level.join(", ")}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A9824A] mb-1.5">{t("cardFormat")}</div>
                  <div className="text-[13px] text-[#F5F1EA]">{product.format.join(", ")}</div>
                </div>
                <div className="col-span-2">
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A9824A] mb-1.5">{t("cardEnvironment")}</div>
                  <div className="text-[13px] text-[#F5F1EA]">{product.environment}</div>
                </div>
              </div>

              {/* Key features */}
              <div className="mb-7">
                <h3 className="font-display text-lg font-bold text-[#F5F1EA] mb-3">{t("cardKeyFeatures")}</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {content?.features?.map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-[13px] text-[#D8DCE2]/80">
                      <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: accent }} />
                      {f}
                    </div>
                  ))}
                </div>
              </div>

              {/* How it works */}
              <div className="mb-7">
                <h3 className="font-display text-lg font-bold text-[#F5F1EA] mb-3">{t("cardHowItWorks")}</h3>
                <ol className="space-y-2.5">
                  {content?.howItWorks?.map((step, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13px] text-[#D8DCE2]/80">
                      <span className="font-mono text-[10px] tracking-tight w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${accent}20`, color: accent }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Best for */}
              <div className="mb-8">
                <h3 className="font-display text-lg font-bold text-[#F5F1EA] mb-3">{t("cardBestFor")}</h3>
                <div className="flex flex-wrap gap-1.5">
                  {content?.bestFor?.map((b) => (
                    <span key={b} className="tag tag-silver">{b}</span>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 sticky bottom-0 -mx-6 md:-mx-9 px-6 md:px-9 py-4 bg-[#111216]/95 backdrop-blur border-t border-[#D8DCE2]/10">
                <a
                  href={generateTelegramLink(product.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold flex-1"
                  data-testid="modal-request-access-btn"
                >
                  {content?.cta || t("cardRequestAccess")} <ArrowUpRight size={14} />
                </a>
                <button
                  onClick={() => toggle(product.id)}
                  className={`btn-ghost ${inCompare ? "!border-[#D7B56D]/60 !text-[#D7B56D]" : ""}`}
                  data-testid="modal-compare-btn"
                >
                  {inCompare ? <Check size={14} /> : <Plus size={14} />}
                  {inCompare ? t("cardRemove") : t("cardCompare")}
                </button>
              </div>

              <p className="text-[10px] text-[#D8DCE2]/40 mt-5 leading-relaxed">{t("disclaimer")}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
